import { useState, useEffect, useCallback } from 'react';
import { ledgerApi } from '../api';
import type { LedgerEntry, LedgerSummary, ManualPaymentInput } from '../types';

export function useLedger(boardId: string) {
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [summary, setSummary] = useState<LedgerSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLedger = useCallback(async () => {
    if (!boardId) return;
    setIsLoading(true);
    setError(null);
    try {
      const [entriesData, summaryData] = await Promise.all([
        ledgerApi.getEntries(boardId),
        ledgerApi.getSummary(boardId),
      ]);
      setEntries(entriesData);
      setSummary(summaryData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch ledger');
    } finally {
      setIsLoading(false);
    }
  }, [boardId]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  const recordPayment = async (input: ManualPaymentInput) => {
    const entry = await ledgerApi.recordPayment(boardId, input);
    await fetchLedger();
    return entry;
  };

  return {
    entries,
    summary,
    isLoading,
    error,
    fetchLedger,
    recordPayment,
  };
}
